// src/components/common/EmptyState.js
import React from 'react';
import { FaBox, FaBell, FaComments, FaMapMarkerAlt, FaCar, FaExclamationCircle } from 'react-icons/fa';
import { CustomButton2 } from '../Templates';
import './EmptyState.css';

/**
 * EmptyState - Reusable empty state component for when there's no data to display
 *
 * Shows an icon, message, description, and optional call-to-action button.
 * Helps guide users on what to do next instead of just showing blank space.
 *
 * @param {Object} props
 * @param {string} props.variant - Type of empty state (determines icon and styling)
 *   Options: 'posts', 'packages', 'conversations', 'notifications', 'locations', 'generic'
 * @param {string} props.title - Main message to display
 * @param {string} props.description - Optional secondary description text
 * @param {string} props.actionLabel - Optional button label
 * @param {function} props.onAction - Optional button click handler
 * @param {React.ReactNode} props.icon - Optional custom icon (overrides variant icon)
 * @param {string} props.className - Optional additional CSS class
 */
const EmptyState = ({
  variant = 'generic',
  title,
  description,
  actionLabel,
  onAction,
  icon,
  className = ''
}) => {
  // Icon mapping based on variant
  const getIcon = () => {
    if (icon) return icon;

    const iconProps = { className: 'empty-state__icon-svg' };

    switch (variant) {
      case 'posts':
        return <FaCar {...iconProps} />;
      case 'packages':
        return <FaBox {...iconProps} />;
      case 'conversations':
        return <FaComments {...iconProps} />;
      case 'notifications':
        return <FaBell {...iconProps} />;
      case 'locations':
        return <FaMapMarkerAlt {...iconProps} />;
      case 'generic':
      default:
        return <FaExclamationCircle {...iconProps} />;
    }
  };

  return (
    <div className={`empty-state empty-state--${variant} ${className}`}>
      <div className="empty-state__icon">
        {getIcon()}
      </div>

      <div className="empty-state__content">
        <h3 className="empty-state__title">{title}</h3>

        {description && (
          <p className="empty-state__description">{description}</p>
        )}

        {actionLabel && onAction && (
          <div className="empty-state__action">
            <CustomButton2
              label={actionLabel}
              onClick={onAction}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default EmptyState;
